import { useCallback } from 'react'
import { useRouter } from 'next/router'
import { z } from 'zod'
import qs from 'qs'

import { ProductType } from '@reward-platform/ancillaries-schemas/common'
import { QUERY_PARAMS_PARSE_DEPTH } from '@reward-platform/ancillaries-schemas/common/query-params'

import { useZodValidatedQueryParams } from '~/hooks/useQueryParams/useQueryParams'
import { useStableCreateRoutePath } from '../platform'
import { ProductFilterQueries, ProductFiltersQueriesSchema } from './Filters.types'

const useFilterRouter = <T extends ProductType>(productType: T) => {
  const router = useRouter()
  const createRoutePath = useStableCreateRoutePath()
  const { query } = useZodValidatedQueryParams(
    ProductFiltersQueriesSchema[productType] as unknown as z.ZodType<ProductFilterQueries<T>>
  )

  const pushFiltersQuery = useCallback(
    (nextFilterQuery: ProductFilterQueries<T>, shallowRoute = false) => {
      const rawQuery = router.query as Record<string, string>
      // Deep enough for the car hire vendor desks the results URL carries: at the default
      // depth qs mangles them into `hours[[0]]=...` and writes that back on every filter change.
      const existingQuery = qs.parse(rawQuery, { depth: QUERY_PARAMS_PARSE_DEPTH })
      if (existingQuery.sorting) {
        delete existingQuery.sorting
      }
      if (existingQuery.shoppingFilter) {
        delete existingQuery.shoppingFilter
      }

      const newQuery = qs.stringify({
        ...existingQuery,
        ...nextFilterQuery,
      })
      router.push(
        {
          pathname: createRoutePath(router.pathname),
          query: newQuery,
        },
        undefined,
        { scroll: false, shallow: shallowRoute }
      )
    },
    [router, createRoutePath]
  )

  return { pushFiltersQuery, query }
}

export default useFilterRouter
